import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';

function formatSitePrice(price, currency) {
  if (price == null || price === '') return '—';
  const n = Number(price);
  if (Number.isNaN(n)) return String(price);
  const code = currency || 'CNY';
  return `${code} ${n.toFixed(2)}`;
}

/**
 * 「站点」列全部售卖站点弹窗。
 *
 * 列表里一列最多只放 SITE_PREVIEW_LIMIT 个站点，其余走这里查看，
 * 交互与 SKU 列的「查看更多」一致。
 *
 * 样式刻意用本页自己的 platform-product-sites-modal 前缀（而不是各平台 ops 的
 * spo/tpo/tko-sku-modal）：产品信息页分别跑在 SHEIN/TEMU/TikTok 三个布局下，
 * 只有各自的 ops CSS 会被加载，用别人的前缀会出现「弹窗没样式」。
 */
export default function SitesDetailModal({
  open,
  title = '',
  skcId = '',
  specLabel = '',
  sites = [],
  onClose,
}) {
  const { t } = useTranslation('workbench');
  if (!open) return null;

  const list = Array.isArray(sites) ? sites : [];
  const showPrice = list.some((site) => site?.price != null && site?.price !== '');
  const stop = (event) => {
    event.stopPropagation();
  };

  return createPortal(
    <div
      className="platform-product-sites-modal"
      role="presentation"
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose?.();
      }}
    >
      <div className="platform-product-sites-modal__backdrop" aria-hidden="true" />
      <div
        className="platform-product-sites-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-product-sites-modal-title"
        onClick={stop}
      >
        <div className="platform-product-sites-modal__head">
          <div className="platform-product-sites-modal__head-main">
            <h2 id="platform-product-sites-modal-title">{t('sitesDetailModal.title')}</h2>
            <p className="platform-product-sites-modal__meta">
              {specLabel ? (
                <>
                  <span className="platform-product-sites-modal__meta-text">{specLabel}</span>
                  {skcId && skcId !== '—' ? (
                    <span className="platform-product-sites-modal__meta-sep" aria-hidden="true">·</span>
                  ) : null}
                </>
              ) : null}
              {skcId && skcId !== '—' ? (
                <HoverCopyField
                  label="SKC"
                  value={skcId}
                  separator="："
                  className="platform-product-sites-modal__meta-copy"
                />
              ) : null}
              <span className="platform-product-sites-modal__meta-text">
                {t('sitesDetailModal.siteCount', { count: list.length })}
              </span>
            </p>
            {title ? <p className="platform-product-sites-modal__title">{title}</p> : null}
          </div>
          <button type="button" className="platform-product-sites-modal__btn" onClick={onClose}>
            {t('sitesDetailModal.close')}
          </button>
        </div>
        <div className="platform-product-sites-modal__body">
          {list.length === 0 ? (
            <div className="platform-product-sites-modal__empty">{t('sitesDetailModal.empty')}</div>
          ) : (
            <table className="platform-product-sites-modal__table">
              <thead>
                <tr>
                  <th scope="col">{t('sitesDetailModal.colSite')}</th>
                  {showPrice ? <th scope="col">{t('sitesDetailModal.colSupplyPrice')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {list.map((site, idx) => (
                  <tr key={site?.name ? `${site.name}-${idx}` : idx}>
                    <td>{site?.name || '—'}</td>
                    {showPrice ? (
                      <td>{formatSitePrice(site?.price, site?.currency)}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="platform-product-sites-modal__foot">
          <button
            type="button"
            className="platform-product-sites-modal__btn platform-product-sites-modal__btn--primary"
            onClick={onClose}
          >
            {t('sitesDetailModal.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
